import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, Fingerprint } from 'lucide-react';
import { API, useAuth } from './AuthContext';
import { isPlatformAuthenticatorAvailable, assertBiometric, getRememberedBiometricEmail, rememberBiometricEmail } from './webauthn';

export default function LoginPage() {
  const { loginWithPassword, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(getRememberedBiometricEmail() || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bioReady, setBioReady] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const avail = await isPlatformAuthenticatorAvailable();
      setBioReady(avail);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const u = await loginWithPassword(email.trim(), password);
      rememberBiometricEmail(u.email);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível entrar. Verifique seus dados.');
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const biometricLogin = async () => {
    setError('');
    if (!email.trim()) {
      setError('Informe seu e-mail para usar a biometria.');
      return;
    }
    setBioBusy(true);
    try {
      const { data: ch } = await axios.get(`${API}/auth/biometric/challenge`, {
        params: { email: email.trim() },
      });
      if (!ch.allowed_credentials || ch.allowed_credentials.length === 0) {
        setError('Nenhuma biometria cadastrada para este e-mail. Faça login e ative em "Ativar biometria".');
        setBioBusy(false);
        return;
      }
      const assertion = await assertBiometric({
        challenge: ch.challenge,
        allowedCredentials: ch.allowed_credentials,
      });
      const { data } = await axios.post(
        `${API}/auth/biometric/verify`,
        { email: email.trim(), credential_id: assertion.credential_id },
        { withCredentials: true }
      );
      try { if (data.token) localStorage.setItem('bp_token', data.token); } catch {}
      setUser(data.user);
      rememberBiometricEmail(data.user.email);
      navigate('/', { replace: true });
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setError('Autenticação biométrica cancelada.');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Falha na biometria.');
      }
    } finally {
      setBioBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg mb-3">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">BioPraga OS</h1>
          <p className="text-sm text-slate-500">Painel Geral & Contratos</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Entrar</h2>
          <p className="text-xs text-slate-500 mb-4">Acesse com seu e-mail corporativo ou conta Google autorizada</p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@empresa.com"
                  className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <p className="text-[12px] text-rose-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="my-4 flex items-center gap-3">
            <span className="flex-1 h-px bg-slate-200"></span>
            <span className="text-[10px] font-semibold text-slate-400">OU</span>
            <span className="flex-1 h-px bg-slate-200"></span>
          </div>

          <div className="space-y-2">
            <button
              onClick={googleLogin}
              className="w-full inline-flex items-center justify-center gap-3 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm font-semibold rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Entrar com Google
            </button>
            {bioReady && (
              <button
                onClick={biometricLogin}
                disabled={bioBusy}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-500 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {bioBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                Entrar com biometria
              </button>
            )}
          </div>

          <p className="text-center text-[10px] text-slate-400 mt-5">
            Novas contas são criadas apenas pelo administrador.
          </p>
        </div>
      </div>
    </div>
  );
}
